import { useTranslation } from 'react-i18next';
import {
  useCompaniesQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
} from 'src/queries';
import { useModal } from 'src/hooks/useModal';
import type { Company } from 'src/types';
import DataTable from '../components/DataTable';
import type { Column } from '../components/DataTable';
import EntityModal from '../components/EntityModal';
import ConfirmDialog from '../components/ConfirmDialog';
import CompanyForm from '../forms/CompanyForm';

const CompaniesTab = () => {
  const { t } = useTranslation();
  const { data: companies = [], isLoading } = useCompaniesQuery();
  const createMutation = useCreateCompanyMutation();
  const updateMutation = useUpdateCompanyMutation();
  const deleteMutation = useDeleteCompanyMutation();

  const formModal = useModal<Company>();
  const deleteModal = useModal<Company>();

  const columns: Column<Company>[] = [
    { key: 'name', header: t('management.company.name') },
    { key: 'address', header: t('management.company.address') },
    { key: 'contact_email', header: t('management.company.contactEmail') },
    { key: 'contact_phone', header: t('management.company.contactPhone') },
  ];

  const handleCreate = (data: Partial<Company>) => {
    createMutation.mutate(data, { onSuccess: () => formModal.close() });
  };

  const handleUpdate = (data: Partial<Company>) => {
    if (!formModal.editingItem) return;
    updateMutation.mutate(
      { id: formModal.editingItem.id, data },
      { onSuccess: () => formModal.close() }
    );
  };

  const handleDelete = () => {
    if (!deleteModal.editingItem) return;
    deleteMutation.mutate(deleteModal.editingItem.id, {
      onSuccess: () => deleteModal.close(),
    });
  };

  const isEditing = !!formModal.editingItem;

  return (
    <>
      <DataTable
        columns={columns}
        data={companies}
        isLoading={isLoading}
        onCreate={formModal.openForCreate}
        onEdit={(company) => formModal.openForEdit(company)}
        onDelete={(company) => deleteModal.openForEdit(company)}
        createLabel={t('management.companies')}
      />

      <EntityModal
        isOpen={formModal.isOpen}
        onClose={formModal.close}
        title={isEditing ? t('management.edit') : t('management.create')}
      >
        <CompanyForm
          initialData={formModal.editingItem ?? undefined}
          onSubmit={isEditing ? handleUpdate : handleCreate}
          isLoading={isEditing ? updateMutation.isPending : createMutation.isPending}
          onCancel={formModal.close}
        />
      </EntityModal>

      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onConfirm={handleDelete}
        onCancel={deleteModal.close}
        title={t('management.confirmDeleteTitle')}
        message={t('management.confirmDeleteMessage', {
          name: deleteModal.editingItem?.name ?? '',
        })}
        isLoading={deleteMutation.isPending}
      />
    </>
  );
};

export default CompaniesTab;
